import kanaExpressionInterpreter from "../../../../../../lib/KanaExpressionInterpreter";
import { GetProjectDetailsResponse } from "../../../../../../service/query";
import { constructContext } from "../../../../../../utils";
import { ProjectDetailsWizardStep } from "../../../../../../widget";
import { Component as ActivityWizardComponent } from "../../../../../developer/activities/activity/types";
import { createEditComponent, createViewComponent } from "../../../../../developer/activities/activity/utils";
import {
  StandardSpecificData,
  StandardSpecificDefinition,
  StandardSpecificStepData,
  Step,
} from "../../overview/components/StandardSpecificSection/types";
import { stepHasDisplayableComponents } from "../../overview/components/StandardSpecificSection/utils";
import { CreateStepFieldsProps, MapStandardSpecificDefinitionToWizardStepsProps, WizardStepMetaData } from "../models";

const isValueFilled = (value: unknown): boolean => {
  if (value === undefined || value === null) {
    return false;
  }

  if (typeof value === "string") {
    return value.trim().length > 0;
  }

  if (Array.isArray(value)) {
    return value.length > 0 && value.some((item) => isValueFilled(item));
  }

  if (typeof value === "object") {
    return Object.values(value).some((item) => isValueFilled(item));
  }

  return true;
};

const isStepDataFilled = (stepData: StandardSpecificStepData | undefined): boolean => {
  if (!stepData) {
    return false;
  }

  if (stepData.data && isValueFilled(stepData.data)) {
    return true;
  }

  return !!stepData.steps?.some((childStepData) => isStepDataFilled(childStepData));
};

export const findStepDataByPath = (
  standardSpecificData: StandardSpecificData | undefined,
  path: string[]
): StandardSpecificStepData | undefined => {
  let currentStepData: StandardSpecificStepData | undefined;
  let currentSteps = standardSpecificData?.steps;

  for (let index = 0; index < path.length; index++) {
    currentStepData = currentSteps?.find((step) => step.key === path[index]);

    if (!currentStepData) {
      return undefined;
    }

    currentSteps = currentStepData.steps;
  }

  return currentStepData;
};

const mapDefinitionToStepMetaData = ({
  definition,
  standardSpecificData,
  expressionContext,
}: MapStandardSpecificDefinitionToWizardStepsProps): WizardStepMetaData[] => {
  const displayableTopLevelSteps =
    definition?.steps?.filter((step: Step) => stepHasDisplayableComponents(step, expressionContext)) ?? [];

  return displayableTopLevelSteps.map((step) => {
    const displayableSubSteps =
      step.steps?.filter((subStep: Step) => stepHasDisplayableComponents(subStep, expressionContext)) ?? [];

    const stepData = findStepDataByPath(standardSpecificData, [step.key]);
    const isTopLevelStepFilled = isStepDataFilled(stepData);

    return {
      key: step.key,
      stepHeader: step.label,
      isFilled: isTopLevelStepFilled,
      subSteps: displayableSubSteps.map((subStep) => {
        const subStepData = findStepDataByPath(standardSpecificData, [step.key, subStep.key]);

        return {
          key: subStep.key,
          stepName: subStep.label,
          isFilled: isStepDataFilled(subStepData),
        };
      }),
    };
  });
};

const getCurrentStepKeys = (steps: WizardStepMetaData[]): string[] => {
  for (let stepIndex = 0; stepIndex < steps.length; stepIndex++) {
    const step = steps[stepIndex];

    if (step.subSteps.length > 0) {
      const firstIncompleteSubStep = step.subSteps.find((subStep) => !subStep.isFilled);

      if (firstIncompleteSubStep) {
        return [step.key, firstIncompleteSubStep.key];
      }
    } else if (!step.isFilled) {
      return [step.key];
    }
  }

  const firstStepWithSubSteps = steps.find((step) => step.subSteps.length > 0);
  if (firstStepWithSubSteps) {
    return [firstStepWithSubSteps.key, firstStepWithSubSteps.subSteps[0].key];
  }

  return steps[0] ? [steps[0].key] : [];
};

const getSelectedCurrentStepKeys = (steps: WizardStepMetaData[], selectedStepKeys: string[] | undefined): string[] => {
  if (!selectedStepKeys || selectedStepKeys.length === 0) {
    return getCurrentStepKeys(steps);
  }

  const selectedStep = steps.find((step) => step.key === selectedStepKeys[0]);
  if (!selectedStep) {
    return getCurrentStepKeys(steps);
  }

  if (selectedStep.subSteps.length === 0) {
    return [selectedStep.key];
  }

  const selectedSubStep = selectedStep.subSteps.find((subStep) => subStep.key === selectedStepKeys[1]);
  if (selectedSubStep) {
    return [selectedStep.key, selectedSubStep.key];
  }

  return [selectedStep.key, selectedStep.subSteps[0].key];
};

export const areStepKeysEqual = (leftStepKeys: string[], rightStepKeys: string[]): boolean =>
  leftStepKeys.length === rightStepKeys.length &&
  leftStepKeys.every((leftStepKey, keyIndex) => leftStepKey === rightStepKeys[keyIndex]);

export const getDefaultSelectedStepKeys = (steps: ProjectDetailsWizardStep[]): string[] => {
  if (steps.length === 0) {
    return [];
  }

  const currentStep = steps.find((step) => step.current);
  if (!currentStep) {
    const firstStep = steps[0];
    if (firstStep.subSteps.length > 0) {
      return [firstStep.key, firstStep.subSteps[0].key];
    }

    return [firstStep.key];
  }

  const currentSubStep = currentStep.subSteps.find((subStep) => subStep.current);
  if (currentSubStep) {
    return [currentStep.key, currentSubStep.key];
  }

  if (currentStep.subSteps.length > 0) {
    return [currentStep.key, currentStep.subSteps[0].key];
  }

  return [currentStep.key];
};

export const normalizeSelectedStepKeys = (steps: ProjectDetailsWizardStep[], selectedStepKeys: string[]): string[] => {
  if (steps.length === 0) {
    return [];
  }

  if (selectedStepKeys.length === 0) {
    return getDefaultSelectedStepKeys(steps);
  }

  const selectedTopLevelStep = steps.find((step) => step.key === selectedStepKeys[0]);
  if (!selectedTopLevelStep) {
    return getDefaultSelectedStepKeys(steps);
  }

  if (selectedStepKeys.length === 2) {
    const selectedSubStep = selectedTopLevelStep.subSteps.find((subStep) => subStep.key === selectedStepKeys[1]);
    if (selectedSubStep) {
      return selectedStepKeys;
    }
  } else if (selectedTopLevelStep.subSteps.length === 0) {
    return selectedStepKeys;
  }

  if (selectedTopLevelStep.subSteps.length > 0) {
    return [selectedTopLevelStep.key, selectedTopLevelStep.subSteps[0].key];
  }

  return [selectedTopLevelStep.key];
};

export const getOrderedStepKeys = (steps: ProjectDetailsWizardStep[]): string[][] =>
  steps.flatMap((step) =>
    step.subSteps.length > 0 ? step.subSteps.map((subStep) => [step.key, subStep.key]) : [[step.key]]
  );

export const findDefinitionStepByKeys = (
  definition: StandardSpecificDefinition | undefined,
  stepKeys: string[]
): Step | undefined => {
  if (!definition?.steps || stepKeys.length === 0) {
    return undefined;
  }

  let currentStep: Step | undefined;
  let currentSteps: Step[] | undefined = definition.steps;

  for (let stepIndex = 0; stepIndex < stepKeys.length; stepIndex++) {
    currentStep = currentSteps?.find((step) => step.key === stepKeys[stepIndex]);

    if (!currentStep) {
      return undefined;
    }

    currentSteps = currentStep.steps;
  }

  return currentStep;
};

export const isDefinitionStepVisible = (
  definitionStep: Step | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): boolean => {
  if (!definitionStep) {
    return false;
  }

  return stepHasDisplayableComponents(definitionStep, expressionContext);
};

export const getVisibleChildSteps = (
  definitionStep: Step | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): Step[] => {
  if (!definitionStep?.steps) {
    return [];
  }

  return definitionStep.steps.filter((step) => stepHasDisplayableComponents(step, expressionContext));
};

export const mapStandardSpecificDefinitionToWizardSteps = ({
  definition,
  standardSpecificData,
  selectedStepKeys,
  expressionContext,
}: MapStandardSpecificDefinitionToWizardStepsProps): ProjectDetailsWizardStep[] => {
  const mappedSteps = mapDefinitionToStepMetaData({
    definition,
    standardSpecificData,
    expressionContext,
  });

  if (mappedSteps.length === 0) {
    return [];
  }

  const currentStepKeys = getSelectedCurrentStepKeys(mappedSteps, selectedStepKeys);

  return mappedSteps.map((step) => ({
    key: step.key,
    stepHeader: step.stepHeader,
    current: currentStepKeys[0] === step.key,
    valid: step.isFilled,
    subSteps: step.subSteps.map((subStep) => ({
      key: subStep.key,
      stepName: subStep.stepName,
      current: currentStepKeys[0] === step.key && currentStepKeys[1] === subStep.key,
      valid: subStep.isFilled,
      inProgress: false,
    })),
  }));
};

export const filterConditionalStep = (
  step: Step,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): Step | undefined => {
  if (step.conditionExpression && !kanaExpressionInterpreter(step.conditionExpression, expressionContext)) {
    return undefined;
  }

  const filteredChildSteps =
    step.steps
      ?.map((childStep) => filterConditionalStep(childStep, expressionContext))
      .filter((childStep): childStep is Step => childStep !== undefined) ?? [];

  return {
    ...step,
    steps: filteredChildSteps.length > 0 ? filteredChildSteps : undefined,
  };
};

export const filterConditionalSteps = (
  definition: StandardSpecificDefinition | undefined,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  expressionContext: any
): StandardSpecificDefinition | undefined => {
  if (!definition?.steps) {
    return definition;
  }

  return {
    ...definition,
    steps: definition.steps
      .map((step) => filterConditionalStep(step, expressionContext))
      .filter((step): step is Step => step !== undefined),
  };
};

const createStepDataBranch = (
  currentPathKey: string,
  remainingPath: string[],
  key: string,
  value: unknown
): StandardSpecificStepData => {
  if (remainingPath.length === 0) {
    return {
      key: currentPathKey,
      data: {
        [key]: value,
      },
    };
  }

  const [nextPathKey, ...nextRemainingPath] = remainingPath;

  return {
    key: currentPathKey,
    steps: [createStepDataBranch(nextPathKey, nextRemainingPath, key, value)],
  };
};

const updateStepDataByPath = (
  steps: StandardSpecificStepData[] | undefined,
  path: string[],
  key: string,
  value: unknown
): StandardSpecificStepData[] => {
  const existingSteps = steps ?? [];

  if (path.length === 0) {
    return existingSteps;
  }

  const [currentPathKey, ...remainingPath] = path;
  const currentStep = existingSteps.find((step) => step.key === currentPathKey);

  if (!currentStep) {
    return [...existingSteps, createStepDataBranch(currentPathKey, remainingPath, key, value)];
  }

  return existingSteps.map((step) => {
    if (step.key !== currentPathKey) {
      return step;
    }

    if (remainingPath.length === 0) {
      return {
        ...step,
        data: {
          ...(step.data ?? {}),
          [key]: value,
        },
      };
    }

    return {
      ...step,
      steps: updateStepDataByPath(step.steps as StandardSpecificStepData[] | undefined, remainingPath, key, value),
    };
  });
};

export const setStepDataValue = (
  standardSpecificData: StandardSpecificData,
  path: string[],
  key: string,
  value: unknown
): StandardSpecificData => ({
  ...standardSpecificData,
  steps: updateStepDataByPath(
    (standardSpecificData.steps as StandardSpecificStepData[] | undefined) ?? [],
    path,
    key,
    value
  ),
});

export const createStepFields = ({
  selectedStep,
  selectedDataPath,
  selectedStepData,
  isEditable,
  expressionContext,
  onFieldChange,
}: CreateStepFieldsProps): JSX.Element[] => {
  if (!selectedStep?.components) {
    return [];
  }

  const values = selectedStepData?.data ?? {};

  return selectedStep.components
    .map((component) => {
      if (isEditable) {
        return createEditComponent(
          component as unknown as ActivityWizardComponent,
          values,
          undefined,
          onFieldChange,
          expressionContext,
          selectedDataPath,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          {
            enableDiscussions: false,
            enableFileUpload: false,
          }
        );
      }

      return createViewComponent(
        component as unknown as ActivityWizardComponent,
        undefined,
        values,
        expressionContext,
        [],
        selectedDataPath
      );
    })
    .filter((component): component is JSX.Element => component !== null);
};

export const createExpressionContext = (
  privateStandardSpecificData: StandardSpecificData,
  projectDetails: GetProjectDetailsResponse | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  return { privateStandardSpecific: constructContext(privateStandardSpecificData), project: projectDetails };
};
